import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Typography,
  Avatar,
  Sheet,
  Skeleton,
  Chip,
  Divider,
  Grid,
  IconButton,
  Tooltip
} from "@mui/joy";
import InfoIcon from "@mui/icons-material/Info";
import AlarmIcon from "@mui/icons-material/Alarm";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { format, formatDistanceToNow, parseISO } from "date-fns";

import { supabase } from "../supabaseClient";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { RelationshipType, RelationshipTypeLabels } from "../types";
import { CheckinCheckoutRecordView } from "../types/CheckinCheckoutRecord";
import {
  fetchProfilePicture,
  getInitials,
  formatStudentName,
  getStudentBirthdayStatus,
  getClassEndTime,
  getLocalDateString,
} from "../utils";
import { EnrollmentType } from "../types/Class";

interface NotCheckedInStudent {
  id: string;
  first_name: string;
  last_name: string;
  preferred_first_name?: string;
  date_of_birth?: string;
  profile_picture?: string;
  enrollment_type?: EnrollmentType;
  class_schedule: {
    start: string;
    end: string;
    display: string;
  };
}

interface EnrolledStudent {
  enrollment_type?: string;
  student: {
    id: string;
    first_name: string;
    last_name: string;
    preferred_first_name?: string;
    date_of_birth?: string;
    profile_picture_id: string;
  };
  class: {
    schedule: {
      [key: string]: {
        start: string;
        end: string;
        halfEnd?: string;
        hasSchool: boolean;
        supportsHalf?: boolean;
      };
    };
  };
}

const DashboardPage: React.FC = () => {
  const [checkins, setCheckins] = useState<CheckinCheckoutRecordView[]>([]);
  const [checkouts, setCheckouts] = useState<CheckinCheckoutRecordView[]>([]);
  const [loading, setLoading] = useState(true);
  const [newRecords, setNewRecords] = useState<{ [id: string]: boolean }>({});
  const [notCheckedInStudents, setNotCheckedInStudents] = useState<NotCheckedInStudent[]>([]);
  const [studentSchedules, setStudentSchedules] = useState<Record<string, any>>({});

  // Helper function to count unique students
  const countUniqueStudents = (records: CheckinCheckoutRecordView[]) => {
    const uniqueStudentIds = new Set(records.map(record => record.student.id));
    return uniqueStudentIds.size;
  };

  // Helper function to check if a student has any classes with school today
  const getStudentTodaySchedule = async (studentId: string) => {
    try {
      const today = new Date().toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
      
      const { data: studentClasses, error } = await supabase
        .from("classes_students")
        .select(`
          enrollment_type,
          class:class_id(
            schedule
          )
        `)
        .eq("student_id", studentId);

      if (error) throw error;

      // Find any class that has school today
      for (const enrollment of (studentClasses as any[]) || []) {
        const schedule = enrollment.class?.schedule?.[today];
        if (schedule?.hasSchool && schedule?.start) {
          const formattedStart = format(new Date(`1970-01-01T${schedule.start}`), "h:mm a");
          
          // Use the new utility to get the appropriate end time
          const endTime = getClassEndTime(enrollment.class, today, enrollment.enrollment_type || "full");
          if (!endTime) continue;
          
          const formattedEnd = format(new Date(`1970-01-01T${endTime}`), "h:mm a");
          return {
            start: schedule.start,
            end: endTime,
            display: `${today.slice(0, 1).toUpperCase()}${today.slice(1, 3).toLowerCase()}: ${formattedStart} - ${formattedEnd}`
          };
        }
      }
      
      return null;
    } catch (error) {
      console.error("Error fetching student schedule:", error);
      return null;
    }
  };

  const fetchCheckInOutRecords = async (id: string | null = null) => {
    // Only show the loading spinner if we're fetching all records, if we are just adding one, we don't need to show the spinner.
    if (!id) {
      setLoading(true);
    }

    try {
      const today = new Date();
      const startOfDay = new Date(today.setHours(0, 0, 0, 0)).toISOString();
      const endOfDay = new Date(today.setHours(23, 59, 59, 999)).toISOString();

      let query = supabase
        .from("checkin_checkout_records_view")
        .select(`
          *,
          student:student_id(
            id,
            first_name,
            last_name,
            profile_picture_id,
            date_of_birth
          )
        `);

      if (id) {
        // Fetch a single record by ID
        query = query.eq("id", id);
      } else {
        // Fetch all records for today
        query = query
          .gte("created_at", startOfDay)
          .lte("created_at", endOfDay)
          .order("created_at", { ascending: false });
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      const formattedRecords = await Promise.all(
        (data || []).map(async (record: any) => {
          const profilePicture = await fetchProfilePicture("students", record.student.profile_picture_id);

          return {
            id: record.id,
            action: record.action,
            signature: record.signature,
            created_at: record.created_at,
            student: {
              id: record.student.id,
              first_name: record.student.first_name,
              last_name: record.student.last_name,
              profile_picture: profilePicture,
              date_of_birth: record.student.date_of_birth,
            },
            parent_guardian: {
              id: record.parent_guardian_id,
              first_name: record.parent_guardian_first_name,
              last_name: record.parent_guardian_last_name,
              relationship: record.parent_guardian_relationship,
            },
            class: {
              id: record.class_id,
                          schedule: record.schedule,
            start_month: record.start_month,
            start_year: record.start_year,
            end_month: record.end_month,
            end_year: record.end_year,
            },
            enrollment_type: record.enrollment_type,
            teachers: {
              primary: record.primary_teacher_first_name
                ? {
                    first_name: record.primary_teacher_first_name,
                    last_name: record.primary_teacher_last_name,
                  }
                : undefined,
              secondary: record.secondary_teacher_first_name
                ? {
                    first_name: record.secondary_teacher_first_name,
                    last_name: record.secondary_teacher_last_name,
                  }
                : undefined,
            },
          } as CheckinCheckoutRecordView;
        })
      );

      if (id) {
        setNewRecords((prev) => ({ ...prev, [updatedRecord.id]: true }));

        setTimeout(() => {
          setNewRecords((prev) => {
            const { [updatedRecord.id]: _, ...rest } = prev;
            return rest;
          });
        }, 30000);

        // Update or add the single record in the state
        const updatedRecord = formattedRecords[0];
        if (updatedRecord) {
          setCheckins((prev) =>
            updatedRecord.action === "checkin"
              ? prev.some((r) => r.id === updatedRecord.id)
                ? prev.map((r) => (r.id === updatedRecord.id ? updatedRecord : r))
                : [updatedRecord, ...prev]
              : prev
          );

          setCheckouts((prev) =>
            updatedRecord.action === "checkout"
              ? prev.some((r) => r.id === updatedRecord.id)
                ? prev.map((r) => (r.id === updatedRecord.id ? updatedRecord : r))
                : [updatedRecord, ...prev]
              : prev
          );
        }
      } else {
        // Set all records
        setCheckins(formattedRecords.filter((r) => r.action === "checkin"));
        setCheckouts(formattedRecords.filter((r) => r.action === "checkout"));
      }
    } catch (error) {
      console.error("Error fetching check-in/out records:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotCheckedInStudents = async () => {
    try {
      const today = new Date().toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
      
      // Get all students with classes today
      const { data: studentsData, error: studentsError } = await supabase
        .from("classes_students")
        .select(`
          enrollment_type,
          student:student_id(
            id,
            first_name,
            last_name,
            preferred_first_name,
            date_of_birth,
            profile_picture_id
          ),
          class:class_id(
            schedule
          )
        `);

      if (studentsError) throw studentsError;

      // Get all checked in students for today
      const startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date();
      endOfDay.setHours(23, 59, 59, 999);

      const { data: checkedInData, error: checkedInError } = await supabase
        .from("checkin_checkout_records_view")
        .select("student_id")
        .eq("action", "checkin")
        .gte("created_at", startOfDay.toISOString())
        .lte("created_at", endOfDay.toISOString());

      if (checkedInError) throw checkedInError;

      // Create a set of checked-in student IDs
      const checkedInStudentIds = new Set(checkedInData.map(record => record.student_id));

      // Filter and format students who haven't checked in
      const notCheckedIn = await Promise.all(
        ((studentsData as unknown) as EnrolledStudent[] || [])
          .filter(student => {
            const schedule = student.class?.schedule?.[today];
            return schedule?.hasSchool && !checkedInStudentIds.has(student.student.id);
          })
          .map(async student => {
            if (!student.class?.schedule?.[today]) {
              return null;
            }

            const schedule = student.class.schedule[today];
            const profilePicture = await fetchProfilePicture("students", student.student.profile_picture_id);
            
            const formattedStart = format(new Date(`1970-01-01T${schedule.start}`), "h:mm a");
            
            // Use the new utility to get the appropriate end time
            const endTime = getClassEndTime(student.class, today, (student.enrollment_type as "full" | "half") || "full");
            if (!endTime) return null;
            
            const formattedEnd = format(new Date(`1970-01-01T${endTime}`), "h:mm a");
            
            return {
              id: student.student.id,
              first_name: student.student.first_name,
              last_name: student.student.last_name,
              preferred_first_name: student.student.preferred_first_name,
              date_of_birth: student.student.date_of_birth,
              profile_picture: profilePicture || undefined,
              enrollment_type: (student.enrollment_type as "full" | "half") || "full",
              class_schedule: {
                start: schedule.start,
                end: endTime,
                display: `${today.slice(0, 1).toUpperCase()}${today.slice(1, 3).toLowerCase()}: ${formattedStart} - ${formattedEnd}`
              }
            } as NotCheckedInStudent;
          })
      );

      setNotCheckedInStudents(notCheckedIn.filter((student): student is NotCheckedInStudent => student !== null));
    } catch (error) {
      console.error("Error fetching not checked in students:", error);
    }
  };

  useEffect(() => {
    fetchCheckInOutRecords(); // Fetch all records initially

    const subscription = supabase
      .channel("public:checkin_checkout_records")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "checkin_checkout_records" },
        (payload: any) => {
          // console.log("Payload: ", payload);
          const recordId = payload.new?.id || payload.old?.id;

          if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
            // Fetch or update the single record
            fetchCheckInOutRecords(recordId);
          } else if (payload.eventType === "DELETE") {
            // Remove the record from the state; we check checkin/checkout because we don't know where the id corresponds to.
            setCheckins((prev) => prev.filter((record) => record.id !== recordId));
            setCheckouts((prev) => prev.filter((record) => record.id !== recordId));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, []);

  // This effect will run every 30 seconds to update the time ago for each record
  useEffect(() => {
    const interval = setInterval(() => {
      // Trigger re-render for timeago
      setCheckins((prev) => [...prev]);
      setCheckouts((prev) => [...prev]);
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, []);

  useEffect(() => {
    fetchNotCheckedInStudents();
  }, [checkins]); // Refresh when checkins change

  const renderRecord = (record: CheckinCheckoutRecordView) => {
    const isNew = newRecords[record.id];
    const timeAgo = formatDistanceToNow(new Date(record.created_at), { addSuffix: true });
    const formattedTime = format(new Date(record.created_at), "h:mm a");
    const birthdayStatus = getStudentBirthdayStatus(record.student.date_of_birth);

    // Use the actual check-in date to determine the day of week, not current date
    const checkinDate = new Date(record.created_at);
    const checkinDayName = checkinDate.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();

    // Check if we have a cached schedule for this student
    const cachedSchedule = studentSchedules[record.student.id];
    
    // If no cached schedule, fetch it (this will trigger a re-render when the schedule is loaded)
    if (!cachedSchedule) {
      getStudentTodaySchedule(record.student.id).then(schedule => {
        if (schedule) {
          setStudentSchedules(prev => ({ ...prev, [record.student.id]: schedule }));
        }
      });
    }

    // Use cached schedule if available, otherwise fall back to the class-specific schedule
    const todaySchedule = cachedSchedule || (record.class.schedule
      ? Object.entries(record.class.schedule)
          .filter(([day, { start, hasSchool }]: any[]) => 
            hasSchool && start && day.toLowerCase() === checkinDayName // Match the check-in day's schedule
          )
          .map(([day, { start }]: any[]) => {
            const formattedStart = format(new Date(`1970-01-01T${start}`), "h:mm a");
            
            // Use the new utility to get the appropriate end time
            const endTime = getClassEndTime(record.class, checkinDayName, record.enrollment_type || "full");
            if (!endTime) return null;
            
            const formattedEnd = format(new Date(`1970-01-01T${endTime}`), "h:mm a");
            return { start, end: endTime, display: `${day.slice(0, 1).toUpperCase()}${day.slice(1, 3).toLowerCase()}: ${formattedStart} - ${formattedEnd}` };
          })[0] // We only need today's schedule
      : null);

    // Calculate check-in timing if applicable (only for check-ins)
    let checkinTiming = null;
    
    // Debug: Check if schedule was found
    console.log("Schedule lookup debug:", {
      studentName: record.student.first_name,
      checkinDayName,
      hasClassSchedule: !!record.class.schedule,
      classScheduleKeys: record.class.schedule ? Object.keys(record.class.schedule) : [],
      foundTodaySchedule: !!todaySchedule,
      todaySchedule
    });
    
    if (todaySchedule && record.action === "checkin") {
      const checkInTime = new Date(record.created_at);
      const checkInDate = getLocalDateString(record.created_at); // Extract the date part using local timezone, e.g., "2023-10-03"
      
      // Create the scheduled start time for the same date as the check-in
      // Ensure the time format includes seconds for proper parsing
      const startTimeFormatted = todaySchedule.start.includes(':') ? todaySchedule.start + ':00' : todaySchedule.start;
      const endTimeFormatted = todaySchedule.end.includes(':') ? todaySchedule.end + ':00' : todaySchedule.end;
      
      const scheduledStartTime = new Date(`${checkInDate}T${startTimeFormatted}`);
      
      // Create the scheduled end time for comparison
      const scheduledEndTime = new Date(`${checkInDate}T${endTimeFormatted}`);
      
      // Calculate difference from start time
      const minutesFromStart = Math.round(
        (checkInTime.getTime() - scheduledStartTime.getTime()) / (1000 * 60)
      );
      
      // Calculate difference from end time  
      const minutesFromEnd = Math.round(
        (checkInTime.getTime() - scheduledEndTime.getTime()) / (1000 * 60)
      );
      
      // Determine timing status with improved logic
      if (minutesFromStart <= -30) {
        // More than 30 minutes before start = early
        checkinTiming = { type: "early", minutes: Math.abs(minutesFromStart) };
      } else if (minutesFromStart <= 30) {
        // Within 30 minutes of start time (before or after) = on time
        checkinTiming = { type: "onTime", minutes: 0 };
      } else if (minutesFromEnd <= 0) {
        // After start but before end = late but reasonable
        checkinTiming = { type: "late", minutes: minutesFromStart };
      } else {
        // After class end time = very late
        checkinTiming = { type: "late", minutes: minutesFromStart };
      }
    }

    // checkinTiming = { type: "late", minutes: 5 }; // Uncomment this line to test the timing chip

    const teacherLine = `with ${
      record.teachers.primary
        ? `${record.teachers.primary.first_name} ${record.teachers.primary.last_name}`
        : "No Primary Teacher"
    }${
      record.teachers.secondary
        ? ` and ${record.teachers.secondary.first_name} ${record.teachers.secondary.last_name}`
        : ""
    }`;

    return (
      <Sheet
        key={record.id}
        variant="outlined"
        sx={{
          p: 2,
          display: "flex",
          gap: 2,
          alignItems: "center",
          mb: 2,
          borderRadius: "sm",
          backgroundColor: isNew
            ? checkinTiming?.type === "late"
              ? "rgba(255, 99, 71, 0.3)" // Light red for late and new
              : "rgba(144, 238, 144, 0.3)" // Light green for new
            : "inherit", // Default for neither
        }}
      >
        <Avatar
          src={record.student.profile_picture || undefined}
          sx={{ width: 64, height: 64 }}
        >
          {!record.student.profile_picture
            ? getInitials({ first_name: record.student.first_name, last_name: record.student.last_name })
            : null}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography level="h4">
            {formatStudentName(record.student)}{" "}
            <Typography color="neutral">
              was checked {record.action === "checkin" ? "in" : "out"} by {record.parent_guardian.first_name}{" "}
              {record.parent_guardian.last_name} ({RelationshipTypeLabels[record.parent_guardian.relationship as RelationshipType]})
            </Typography>
            {birthdayStatus && (
              <Tooltip title={`Birthdate: ${format(parseISO(record.student.date_of_birth), "MMMM do, yyyy")}`}> 
                <Chip
                  variant="soft"
                  color="success"
                  sx={{
                    ml: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    lineHeight: 1,
                    verticalAlign: "middle",
                  }}
                >
                  🎂 {birthdayStatus === "today" ? "Birthday Today!" : "Birthday Week!"}
                </Chip>
              </Tooltip>
            )}
            {record.enrollment_type === "half" && (
              <Tooltip title="Half Day Enrollment">
                <Chip
                  variant="soft"
                  color="primary"
                  sx={{
                    ml: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    lineHeight: 1,
                    verticalAlign: "middle",
                  }}
                >
                  🕐 Half Day
                </Chip>
              </Tooltip>
            )}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Box>
            <Typography level="body-sm">
              {todaySchedule
                ? `${todaySchedule.display} ${teacherLine}`
                : "No schedule set for today."}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ marginLeft: "auto", textAlign: "right" }}>
          <Typography level="body-sm">{formattedTime}</Typography>
          <Typography level="body-xs" color="neutral">
            {timeAgo}
          </Typography>
          {checkinTiming && checkinTiming.type !== "onTime" && record.action === "checkin" && (
            <Chip
              variant="soft"
              color={checkinTiming.type === "late" ? "danger" : "success"}
              sx={{
                mt: 1,
                display: "inline-flex",
                alignItems: "center",
                lineHeight: 1,
                verticalAlign: "middle",
              }}
            >
              <AlarmIcon style={{ fontSize: "1.2em", verticalAlign: "middle", marginRight: 4 }} />
              <span style={{ verticalAlign: "middle" }}>
                {checkinTiming.minutes} min{checkinTiming.minutes > 1 ? "s" : ""} {checkinTiming.type}
              </span>
            </Chip>
          )}
        </Box>
        <IconButton 
          variant="plain" 
          color="neutral"
          onClick={() => window.location.href = `/students/${record.student.id}`}
          sx={{
            ml: 1,
            alignSelf: "center"
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </Sheet>
    );
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100dvh" }}>
      <Sidebar />
      <Box
        component="main"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          gap: 3,
          p: { xs: 2, sm: 4 },
        }}
      >
        <Header />
        <Box sx={{ mt: 2 }}>
          <Typography level="h2" component="h1" sx={{ mb: 2 }}>
            Dashboard
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 4,
              flexWrap: "wrap",
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography level="h4" sx={{ mb: 2 }}>
                Today's Checkins ({countUniqueStudents(checkins)} students, {checkins.length} checkins)
              </Typography>
              {loading ? (
                <CheckinoutSkeleton />
              ) : checkins.length > 0 ? (
                checkins.map(renderRecord)
              ) : (
                <NoRecords type="check-in" />
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography level="h4" sx={{ mb: 2 }}>
                Today's Checkouts ({countUniqueStudents(checkouts)} students, {checkouts.length} checkouts)
              </Typography>
              {loading ? (
                <CheckinoutSkeleton />
              ) : checkouts.length > 0 ? (
                checkouts.map(renderRecord)
              ) : (
                <NoRecords type="check-out" />
              )}
            </Box>
          </Box>
          {notCheckedInStudents.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography level="h4" sx={{ mb: 2 }}>
                Students Not Checked In Today ({notCheckedInStudents.length})
              </Typography>
              <Grid container spacing={2}>
                {notCheckedInStudents.map((student) => (
                  <Grid key={student.id} xs={12} sm={6} md={4} lg={3}>
                    <Sheet
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        borderRadius: "sm",
                      }}
                    >
                      <Avatar
                        src={student.profile_picture}
                        sx={{ width: 48, height: 48 }}
                      >
                        {!student.profile_picture ? getInitials(student) : null}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                          <Typography level="title-md">
                            {formatStudentName(student)}
                          </Typography>
                          
                          {/* Birthday Badge */}
                          {(() => {
                            const birthdayStatus = getStudentBirthdayStatus(student.date_of_birth || null);
                            if (birthdayStatus) {
                              return (
                                <Tooltip title={`Birthdate: ${student.date_of_birth ? format(parseISO(student.date_of_birth), "MMMM do, yyyy") : "Unknown"}`}>
                                  <Chip
                                    variant="soft"
                                    color="success"
                                    size="sm"
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      lineHeight: 1,
                                    }}
                                  >
                                    🎂 {birthdayStatus === "today" ? "Birthday Today!" : "Birthday Week!"}
                                  </Chip>
                                </Tooltip>
                              );
                            }
                            return null;
                          })()}
                          
                          {/* Half Day Badge */}
                          {student.enrollment_type === "half" && (
                            <Tooltip title="Half Day Enrollment">
                              <Chip
                                variant="soft"
                                color="primary"
                                size="sm"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  lineHeight: 1,
                                }}
                              >
                                🕐 Half Day
                              </Chip>
                            </Tooltip>
                          )}
                        </Box>
                        
                        <Typography level="body-sm" color="neutral">
                          {student.class_schedule.display}
                        </Typography>
                      </Box>
                      <IconButton 
                        variant="plain" 
                        color="neutral"
                        onClick={() => window.location.href = `/students/${student.id}`}
                        sx={{
                          alignSelf: "center"
                        }}
                      >
                        <ChevronRightIcon sx={{ fontSize: 28 }} />
                      </IconButton>
                    </Sheet>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// add a "no records" component that accepts "check-in" or "check-out" as a type.
// this way, we can reuse the component for both check-ins and check-outs.
function NoRecords({ type }: { type: "check-in" | "check-out" }) {
  return (
    <Alert
      key={`no-${type}-records`}
      sx={{ alignItems: "flex-start" }}
      startDecorator={<InfoIcon />}
      variant="soft"
      color="primary"
    >
      No {type}s recorded yet.
    </Alert>
  );
}

function CheckinoutSkeleton({ numberOfRows }: { numberOfRows?: number }) {
  const rowCount = numberOfRows || Math.floor(Math.random() * 3) + 1;

  return (
    <>
      {Array.from({ length: rowCount }).map((_, index) => (
      <Sheet
        key={index}
        variant="outlined"
        sx={{
          p: 1, // Reduce padding further
          display: "flex",
          gap: 1, // Tighten gaps
          alignItems: "center",
          mb: 1, // Reduce spacing between skeleton rows
          borderRadius: "sm",
          height: 100, // Reduce height
        }}
      >
        <Skeleton variant="circular" width={64} height={64} /> {/* Even smaller avatar */}
        <Box sx={{ flex: 1 }}>
          <Skeleton width="70%" height={10} sx={{ mb: 0.5 }} /> {/* Adjust name width */}
          <Skeleton width="50%" height={10} /> {/* Adjust detail width */}
        </Box>
      </Sheet>
      ))}
    </>
  );
}

export default DashboardPage;
